import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../../cmsConfig';
import { GuidanceListItem } from '../../../types/Guidance/GuidanceListItem';
import { GuidancePageTexts } from '../../../types/Guidance/GuidancePageTexts';
import { ApplicationSentByText } from '../../../types/Enums/ApplicationSentBy';
import { GuidanceParticipationDetails } from '../../../types/Guidance/GuidanceParticipationDetails';
import { UpdateGuidanceParticipantInformation } from '../../../types/Guidance/UpdateGuidanceParticipantInformation';
import { ResultOfGuidanceAssignment } from '../../../types/Guidance/ResultOfGuidanceAssignment';
import { GuidanceMeeting } from '../../../types/Guidance/GuidanceMeeting';
import { normalizeActivityDates } from '../../../Functions/DateTime';
import { Activity } from '../../../types/Activity/Activity';
import { EmploymentStatus } from '../../../types/Enums/EmploymentStatus';
import { setUnauthorized } from '../../../redux/reducer/authSlice';
import { HttpStatusCode } from '../../../constants/HttpStatusCode';
import { tsl_projektdeltagande_tsl_statusanstalld } from '../../../types/Enums/Generated/tsl_projektdeltagande_tsl_statusanstalld';

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const toUtcIsoDateFromDateValue = (
  value: Date | string | undefined
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
      return undefined;
    }

    if (DATE_ONLY_REGEX.test(trimmedValue)) {
      return `${trimmedValue}T00:00:00.000Z`;
    }

    const parsedFromString = new Date(trimmedValue);
    if (Number.isNaN(parsedFromString.getTime())) {
      return trimmedValue;
    }

    const year = parsedFromString.getFullYear();
    const month = String(parsedFromString.getMonth() + 1).padStart(2, '0');
    const day = String(parsedFromString.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}T00:00:00.000Z`;
  }

  if (Number.isNaN(value.getTime())) {
    return undefined;
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00.000Z`;
};

const sanitizeGuidanceMeetingPayload = (guidanceMeeting: GuidanceMeeting) => {
  const {
    meetingId,
    coachId,
    participationId,
    vagledareId,
    appointmentDate,
    ...rest
  } =
    guidanceMeeting;

  return {
    ...rest,
    ...(appointmentDate
      ? { appointmentDate: toUtcIsoDateFromDateValue(appointmentDate) }
      : {}),
    ...(meetingId ? { meetingId } : {}),
    ...(coachId ? { coachId } : {}),
    ...(participationId ? { participationId } : {}),
    ...(vagledareId ? { vagledareId } : {}),
  };
};

const sanitizeActivityPayload = (activity: Activity): Activity => {
  const { activityId, modifiedBy, ...rest } = activity;

  return {
    ...rest,
    ...(activityId ? { activityId } : {}),
    ...(modifiedBy ? { modifiedBy } : {}),
  } as Activity;
};
export interface ReactConfig {
  applicationInsightsConnectionString: string;
}

const baseCoachQuery = fetchBaseQuery({
  baseUrl: cmsApiConfig.url + '/guidance',
  headers: {
    'X-CSRF': '1',
  },
  credentials: 'include',
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseCoachQuery(args, api, extraOptions);

  if (result.error?.status === HttpStatusCode.Unauthorized) {
    api.dispatch(setUnauthorized());
  }

  return result;
};

export const guidanceApi = createApi({
  reducerPath: 'guidanceApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'GuidanceTexts',
    'NewParticipants',
    'OngoingParticipants',
    'FinishedParticipants',
    'ParticipantDetails',
  ],
  endpoints: (builder) => ({
    getGuidancePageTexts: builder.query<GuidancePageTexts, void>({
      query: () => ({
        url: `/getPageTexts`,
      }),
      providesTags: ['GuidanceTexts'],
    }),
    getNewParticipants: builder.query<GuidanceListItem[], void>({
      query: () => ({
        url: `/newParticipants`,
      }),
      providesTags: ['NewParticipants'],
      transformResponse: (response: GuidanceListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            applicationSentByText:
              ApplicationSentByText[
                item.applicationSentBy as keyof typeof ApplicationSentByText
              ] || '',
          };
        });
      },
    }),
    getOngoingParticipants: builder.query<GuidanceListItem[], void>({
      query: () => ({
        url: `/ongoingParticipants`,
      }),
      providesTags: ['OngoingParticipants'],
      transformResponse: (response: GuidanceListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            applicationSentByText:
              ApplicationSentByText[
                item.applicationSentBy as keyof typeof ApplicationSentByText
              ] || '',
          };
        });
      },
    }),
    getFinishedParticipants: builder.query<GuidanceListItem[], void>({
      query: () => ({
        url: `/finishedParticipants`,
      }),
      providesTags: ['FinishedParticipants'],
    }),
    getParticipantById: builder.query<GuidanceParticipationDetails, string>({
      query: (id) => ({
        url: `/participant?participantId=${id}`,
      }),
      transformResponse: (
        response: GuidanceParticipationDetails,
        _,
        participantId
      ) => {
        const resultOfGuidanceAssignment =
          response.projectInformation.employmentStatus ===
          EmploymentStatus.FinishedCouldNotReach
            ? {
                resultOfAssignmentChoices:
                  tsl_projektdeltagande_tsl_statusanstalld.Avslutadejgttattn,
                participantId: participantId,
                participantIntents: [],
              }
            : response.resultOfGuidanceAssignment;

        return {
          ...response,
          participant: {
            ...response.participant,
            participantId: participantId,
          },
          resultOfGuidanceAssignment,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    updateParticipantInformation: builder.mutation<
      void,
      {
        id: string;
        body: UpdateGuidanceParticipantInformation;
      }
    >({
      query: ({ id, body }) => ({
        url: `/participantInformation?participantId=${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    finishParticipant: builder.mutation<void, ResultOfGuidanceAssignment>({
      query: (model) => ({
        url: `/finishParticipant`,
        method: 'POST',
        body: model,
      }),
      invalidatesTags: (result, error, model) => {
        return [
          { type: 'ParticipantDetails', id: model.participantId },
          'OngoingParticipants',
          'FinishedParticipants',
        ];
      },
    }),
    saveGuidaceMeeting: builder.mutation<
      void,
      {
        participantId: string;
        guidanceMeeting: GuidanceMeeting;
        activity?: Activity;
      }
    >({
      query: ({ participantId, guidanceMeeting, activity }) => ({
        url: `/saveGuidanceMeeting?participantId=${participantId}`,
        method: 'POST',
        body: {
          guidanceMeeting: sanitizeGuidanceMeetingPayload(guidanceMeeting),
          activity: normalizeActivityDates(
            activity ? sanitizeActivityPayload(activity) : undefined
          ),
        },
      }),
      invalidatesTags: (result, error, { participantId }) => {
        return [
          { type: 'ParticipantDetails', id: participantId },
          'NewParticipants',
          'OngoingParticipants',
          'FinishedParticipants',
        ];
      },
    }),
  }),
});

export const {
  useGetGuidancePageTextsQuery,
  useLazyGetGuidancePageTextsQuery,
  useGetNewParticipantsQuery,
  useLazyGetNewParticipantsQuery,
  useGetOngoingParticipantsQuery,
  useLazyGetOngoingParticipantsQuery,
  useGetFinishedParticipantsQuery,
  useLazyGetFinishedParticipantsQuery,
  useGetParticipantByIdQuery,
  useLazyGetParticipantByIdQuery,
  useUpdateParticipantInformationMutation,
  useFinishParticipantMutation,
  useSaveGuidaceMeetingMutation,
} = guidanceApi;
